'use client';

import { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { Check, Gift, Play, Pause } from 'lucide-react';
import { useTheme } from 'next-themes';
import { AgentAudioVisualizerAura } from '@/components/agents-ui/agent-audio-visualizer-aura';
import { VoiceAgentModal, type MomentoVoice } from '@/components/livekit/VoiceAgentModal';
import { HeroSmartInput } from '@/components/onboarding/HeroSmartInput';
import { HERO_CTA_ENABLED } from '@/lib/onboarding/hero-cta-env';
import { useSupabaseAuth } from '@/lib/auth/simplified/SupabaseAuthContext';
import { ENTITY_SENTENCE, MARKETS_LINE } from '@/lib/marketing/positioning';
import { toast } from 'sonner';

const TESSA_VOICE: MomentoVoice = {
  id: 'Ashley',
  name: 'Tessa',
  description: 'Ready to share something great?',
  avatar: '/voices/tessa.png',
};

const audioSamples = [
  {
    id: 'tessa-question',
    src: '/audio/samples/tessa-question.mp3',
    label: 'Tessa asks — Weekly Sales Check-in',
    duration: '0:07',
    transcript: '“What’s the one deal you’re most worried about this week?”',
  },
  {
    id: 'member-answer',
    src: '/audio/samples/member-answer.mp3',
    label: 'A member answers',
    duration: '0:11',
    transcript: '“Honestly, the Meridian renewal — their champion just left…”',
  },
  {
    id: 'digest-line',
    src: '/audio/samples/digest-line.mp3',
    label: 'Your Monday digest',
    duration: '0:09',
    transcript: '“2 blockers surfaced. Maria needs sign-off by Thursday.”',
  },
];

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- kept for a
// potential lower-fold reuse; unmounted from the hero in the 2026-08-17 declutter.
function HearItWorkCard() {
  const audioRefs = useRef<Record<string, HTMLAudioElement | null>>({});
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [progress, setProgress] = useState<Record<string, number>>({});
  const [failed, setFailed] = useState<Record<string, boolean>>({});

  const handleToggle = (id: string) => {
    const audio = audioRefs.current[id];
    if (!audio || failed[id]) return;

    if (playingId === id) {
      audio.pause();
      setPlayingId(null);
      return;
    }

    // One sample playing at a time — pause any other row first
    for (const [otherId, other] of Object.entries(audioRefs.current)) {
      if (otherId !== id && other && !other.paused) other.pause();
    }

    audio
      .play()
      .then(() => setPlayingId(id))
      .catch((err: unknown) => {
        // Switching samples mid-buffer pauses this element and rejects the
        // pending play() with AbortError — that's an interruption, not a
        // broken file, so the sample must stay playable. Only real media
        // failures (NotSupportedError here, or the <audio> onError handler
        // for missing/undecodable files) degrade to a transcript-only row.
        if ((err as { name?: string } | null)?.name === 'AbortError') {
          setPlayingId((current) => (current === id ? null : current));
          return;
        }
        setFailed((prev) => ({ ...prev, [id]: true }));
        setPlayingId((current) => (current === id ? null : current));
      });
  };

  return (
    <div className="w-full max-w-[400px] bg-background border border-border rounded-2xl shadow-xl p-5 z-10">
      <div className="text-[11px] uppercase tracking-[1.5px] text-[#EF4444] font-space-mono mb-4">
        Hear it work
      </div>

      <div className="space-y-4">
        {audioSamples.map((sample) => {
          const isPlaying = playingId === sample.id;
          const isFailed = !!failed[sample.id];

          return (
            <div key={sample.id} className="flex items-start gap-3">
              <button
                type="button"
                onClick={() => handleToggle(sample.id)}
                disabled={isFailed}
                aria-label={
                  isPlaying ? `Pause sample: ${sample.label}` : `Play sample: ${sample.label}`
                }
                className="w-10 h-10 rounded-full bg-[#EF4444] text-white flex items-center justify-center flex-shrink-0 shadow-[0_2px_8px_rgba(239,68,68,0.35)] hover:bg-[#DC2626] transition-colors disabled:opacity-35 disabled:cursor-not-allowed disabled:hover:bg-[#EF4444] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#EF4444] focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                {isPlaying ? (
                  <Pause className="w-4 h-4" fill="currentColor" />
                ) : (
                  <Play className="w-4 h-4 ml-0.5" fill="currentColor" />
                )}
              </button>

              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-[13px] font-semibold text-foreground truncate">
                    {sample.label}
                  </span>
                  <span className="text-[11px] font-mono text-muted-foreground flex-shrink-0">
                    {sample.duration}
                  </span>
                </div>
                <p className="text-[12px] text-muted-foreground truncate mt-0.5">
                  {sample.transcript}
                </p>
                <div className="h-[3px] bg-muted rounded-full mt-2 overflow-hidden">
                  <div
                    className="h-full bg-[#EF4444] rounded-full transition-[width] duration-150"
                    style={{ width: `${(progress[sample.id] || 0) * 100}%` }}
                  />
                </div>
                <audio
                  ref={(el) => {
                    audioRefs.current[sample.id] = el;
                  }}
                  src={sample.src}
                  preload="metadata"
                  onError={() => {
                    setFailed((prev) => ({ ...prev, [sample.id]: true }));
                    setPlayingId((current) => (current === sample.id ? null : current));
                  }}
                  onTimeUpdate={(e) => {
                    const el = e.currentTarget;
                    if (el.duration) {
                      setProgress((prev) => ({
                        ...prev,
                        [sample.id]: el.currentTime / el.duration,
                      }));
                    }
                  }}
                  onPause={() => {
                    setPlayingId((current) => (current === sample.id ? null : current));
                  }}
                  onEnded={() => {
                    setPlayingId((current) => (current === sample.id ? null : current));
                    setProgress((prev) => ({ ...prev, [sample.id]: 0 }));
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-[11px] text-muted-foreground mt-4 text-center">
        Real product audio · no mic needed
      </p>
    </div>
  );
}

export default function RoutinesHero() {
  const [activeVoice, setActiveVoice] = useState<MomentoVoice | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const auth = useSupabaseAuth();
  const router = useRouter();
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  // Wave H1: the centered "one action owns the fold" layout exists when ANY
  // hero arm can render (call flag OR a message number), not the call alone.
  const callMeEnabled = HERO_CTA_ENABLED;

  const handleOrbClick = async () => {
    if (!auth.isAuthenticated) {
      setActiveVoice(TESSA_VOICE);
      return;
    }
    setIsStarting(true);
    try {
      const res = await fetch('/api/agent/interview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ voiceId: 'Ashley', voiceName: 'Tessa' }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to start');
      const data = await res.json();
      const params = new URLSearchParams({ token: data.token, serverUrl: data.serverUrl });
      router.push(`/rooms/${data.roomName}?${params.toString()}`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to start conversation');
      setIsStarting(false);
    }
  };

  const isDark = mounted && resolvedTheme === 'dark';

  return (
    <section className="relative py-20 md:py-24 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-[-200px] right-[-200px] w-[600px] h-[600px] bg-[radial-gradient(circle,rgba(239,68,68,0.05)_0%,transparent_70%)]" />
      </div>

      <div className="max-w-[1200px] mx-auto px-8">
        {callMeEnabled ? (
          /* Full-width centered hero (2026-08-17, the Explee comparison): the
             phone call IS the demo, so the web-call orb leaves the fold and
             one action owns the whole stage — headline, free-minutes chip,
             input, and the value stated under the action. /try/[voice] keeps
             the live web-call path alive; flag off keeps the pre-launch
             two-column layout untouched. */
          <div className="flex flex-col items-center text-center">
            {/* ICP qualifier up front (the deck's "who buys" lesson: the
                desk-team sale dies — name deskless before the headline). */}
            <div className="inline-flex items-center gap-2 bg-[#ECFDF5] text-[#059669] px-3.5 py-1.5 rounded-full text-[13px] font-semibold mb-6">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
              </svg>
              Meet Tessa — the AI Chief of Staff for deskless teams
            </div>

            <h1 className="max-w-[900px] text-[38px] md:text-[54px] font-bold leading-[1.08] tracking-[-2px] mb-6 text-foreground">
              Stop chasing updates. Tessa reaches out to everyone on your team,{' '}
              <span className="bg-gradient-to-r from-[#EF4444] to-[#F97316] bg-clip-text text-transparent">
                finds what&rsquo;s stuck
              </span>
              , and gets it moving.
            </h1>

            <div className="mb-7 inline-flex items-center gap-2 rounded-full border border-border bg-muted/50 px-4 py-2 text-[14px] font-semibold text-foreground">
              <Gift className="h-4 w-4 flex-shrink-0 text-[#EF4444]" />
              <span>200 free minutes a month — no card required</span>
            </div>

            {/* No template escape hatch here (2026-08-17): one action owns the
                fold. HeroSmartInput surfaces the wizard link itself on the
                error states whose copy promises it — the exit exists exactly
                when the call path fails, and only then. */}
            <HeroSmartInput surface="home_hero" className="max-w-[560px] text-left" />

            <p className="mt-9 max-w-[760px] text-[17px] leading-[1.8] tracking-[0.01em] text-muted-foreground">
              <span className="font-medium text-foreground">Your crews know first.</span> Tessa
              checks in with hotel, construction and plant teams by call or WhatsApp, in their
              language, and routes blockers to{' '}
              <span className="font-medium text-foreground">whoever can fix them, the same day</span>.
              Say hello — <span className="font-medium text-foreground">a text or a call</span>{' '}
              — and your first check-in is drafted from that very conversation:{' '}
              <span className="font-medium text-foreground">you deploy her by talking to her</span>.
            </p>

            <p className="mt-4 max-w-[720px] text-[15px] leading-[1.7] text-muted-foreground">
              {ENTITY_SENTENCE} {MARKETS_LINE}
            </p>

            <div className="mt-7 flex flex-wrap items-center justify-center gap-1.5 text-[13px] text-muted-foreground">
              <Check className="w-3.5 h-3.5 text-[#10B981] flex-shrink-0" />
              Free to start · Your team never pays, never signs up · Anyone can say SKIP
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#ECFDF5] text-[#059669] px-3.5 py-1.5 rounded-full text-[13px] font-semibold mb-6">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
                Meet Tessa — your AI Chief of Staff
              </div>

              <h1 className="text-[42px] md:text-[50px] font-bold leading-[1.08] tracking-[-2px] mb-5 text-foreground">
                Stop chasing updates. Tessa reaches out to everyone on your team,{' '}
                <span className="bg-gradient-to-r from-[#EF4444] to-[#F97316] bg-clip-text text-transparent">
                  finds what&rsquo;s stuck
                </span>
                , and gets it moving.
              </h1>

              <p className="text-[17px] leading-[1.8] text-muted-foreground mb-8 max-w-[480px] tracking-[0.01em]">
                On your cadence, Tessa calls, WhatsApps, Slacks, or emails each person — in English, Spanish, or French — carries what you need them to hear, asks what you need to know, and remembers every conversation. You read one report: trends, blockers, and who can unblock whom.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 mb-5">
                <Link
                  href="/routines/new"
                  className="inline-flex items-center justify-center gap-2 bg-[#EF4444] text-white px-7 py-3.5 rounded-full text-[15px] font-semibold shadow-[0_4px_14px_rgba(239,68,68,0.3)] hover:bg-[#DC2626] hover:-translate-y-0.5 hover:shadow-[0_6px_20px_rgba(239,68,68,0.4)] transition-all"
                >
                  Start your first check-in — free
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </Link>
                <button
                  onClick={handleOrbClick}
                  disabled={isStarting}
                  className="inline-flex items-center justify-center px-7 py-3.5 bg-background text-foreground border border-border rounded-full text-[15px] font-semibold hover:bg-muted transition-all"
                >
                  {isStarting ? 'Connecting...' : 'Experience a check-in'}
                </button>
              </div>

              <div className="flex items-center gap-1.5 text-[13px] text-muted-foreground">
                <Check className="w-3.5 h-3.5 text-[#10B981] flex-shrink-0" />
                Free to start · 200 minutes a month · Your team never pays, never signs up
              </div>
              <p className="mt-1.5 text-[12px] text-muted-foreground/70 max-w-[480px]">
                For your team it&rsquo;s a voice, not a survey — anyone can say SKIP, and what they
                raise reaches the person who can fix it.
              </p>
            </div>

            <div className="relative flex flex-col items-center justify-center gap-10 min-h-[440px]">
              <button
                onClick={handleOrbClick}
                disabled={isStarting}
                className="relative cursor-pointer hover:scale-[1.04] transition-transform duration-300 flex flex-col items-center focus:outline-none"
              >
                <div className={`w-[280px] h-[280px] md:w-[320px] md:h-[320px] rounded-full overflow-hidden flex items-center justify-center ${isDark ? 'bg-gray-950' : 'bg-gray-100'}`}>
                  <AgentAudioVisualizerAura
                    size="xl"
                    state="idle"
                    color="#1FD5F9"
                    colorShift={0.5}
                    themeMode={isDark ? 'dark' : 'light'}
                    className="w-full h-full"
                  />
                </div>
                <div className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 bg-foreground/92 text-background px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap flex items-center gap-2 z-10">
                  <span
                    className="w-2 h-2 bg-[#10B981] rounded-full"
                    style={{ animation: 'pulseDot 2s infinite' }}
                  />
                  {isStarting ? 'Connecting...' : 'Experience a check-in — talk to Tessa'}
                </div>
              </button>
            </div>
          </div>
        )}
      </div>

      {activeVoice && (
        <VoiceAgentModal
          open={!!activeVoice}
          onOpenChange={(isOpen) => { if (!isOpen) setActiveVoice(null); }}
          voice={activeVoice}
        />
      )}
    </section>
  );
}
